/**
 * Headless presentation for a merchandise draft estimate: state labels and
 * tones, the price build-up rows, how long a ticket has been open, and why
 * sending is unavailable. Shared by the estimate detail page's merchandise
 * state and the Promo Office lane so the two never disagree on a word or a
 * figure.
 *
 * **No business rules live here.** The buy total, the margin and the price all
 * come from the backend (`pricing.buyTotalPence`,
 * `costChange.currentBuyTotalPence`) — the helpers that used to recompute them
 * moved to `mock-data/merchandise.ts`, where building a fixture is their only
 * remaining job. Nothing in this file branches on `sentAt`: it is permanently
 * null in slice A, so such a branch is dead code, not a safeguard.
 */
import type {
  MerchandiseDraftEstimate,
  MerchandiseTeamTicket,
  DraftEstimatePricing,
  DraftEstimateState,
} from "../data/merchandise-types";
import type { MoneyRow } from "./estimate-presentation";
import { formatDateOrDash, formatQuotedPrice } from "./formatters";
import { currentTime } from "./current-time";

export type DraftEstimateTone = "neutral" | "success" | "warning";

/**
 * The state words a CSR reads. A linked request is "Quoted": it carries the
 * merchandise team's price and is no longer just a draft (Matt, 15 Oct 2026).
 * The stored states keep their names (`priced`, `cost_changed`).
 */
export const DRAFT_ESTIMATE_STATE_LABELS: Record<DraftEstimateState, string> = {
  awaiting_quote: "Awaiting quote",
  priced: "Quoted",
  cost_changed: "Quote changed",
  withdrawn: "Withdrawn",
};

export function draftEstimateStateTone(
  state: DraftEstimateState,
): DraftEstimateTone {
  switch (state) {
    case "priced":
      return "success";
    case "cost_changed":
      return "warning";
    case "awaiting_quote":
    case "withdrawn":
      return "neutral";
  }
}

/** "1 request line", "3 request lines" — how a draft's size is summarised
 *  wherever its lines are not listed in full. */
export function describeRequestLineCount(count: number): string {
  return `${count} request line${count === 1 ? "" : "s"}`;
}

/** The ticket number as people say it: "#5102", never "#tkt-5102". */
function ticketNumber(ticketId: string): string {
  return `#${ticketId.replace(/^tkt-/, "")}`;
}

export interface DraftEstimateOutcome {
  label: "Draft" | "Quoted";
  caption: string;
}

/**
 * The Estimate outcome block: a plain "Draft" until the merchandise team's
 * quote is attached, then "Quoted" — never the amber "Pending" an open
 * Tharstern estimate wears. Null once withdrawn: there is no outcome left to
 * describe.
 */
export function draftEstimateOutcome(
  draft: MerchandiseDraftEstimate,
): DraftEstimateOutcome | null {
  if (draft.state === "withdrawn") return null;
  switch (draft.state) {
    case "awaiting_quote":
      return {
        label: "Draft",
        caption: "Not ready to send until a quote is attached",
      };
    case "priced":
      return {
        label: "Quoted",
        caption: "At the merchandise team's Promo Office price",
      };
    case "cost_changed":
      return {
        label: "Quoted",
        caption: "Held — the Promo Office quote changed; re-price or withdraw",
      };
  }
}

export interface DraftEstimateLifecycleDetail {
  lead: string;
  /** A ticket to link to, with the words that carry the link. */
  ticket: { id: string; label: string } | null;
  tail: string | null;
}

/** The line under the lifecycle badge: what is happening and whose ticket it is on. */
export function draftEstimateLifecycleDetail(
  draft: MerchandiseDraftEstimate,
  now: number = currentTime().getTime(),
): DraftEstimateLifecycleDetail {
  switch (draft.state) {
    case "awaiting_quote": {
      // `null` when ticket creation failed, which is a real state rather than a
      // bug: the request still stands, because losing a CSR's enquiry over a
      // ticketing failure would be worse than raising no ticket.
      const ticket = draft.merchandiseTeamTicket;
      return {
        lead: "Waiting for the merchandise team to attach their Promo Office quote",
        ticket:
          ticket && ticket.raisedAt
            ? {
                id: ticket.id,
                label: `ticket ${ticketNumber(ticket.id)} (open ${describeOpenFor(ticket.raisedAt, now)})`,
              }
            : null,
        tail: describeTicketClaim(ticket),
      };
    }
    case "priced": {
      const quote = draft.linkedQuote?.promoOfficeQuoteNumber ?? "not recorded";
      const pricedOn = draft.pricing
        ? formatDateOrDash(draft.pricing.pricedAt)
        : "-";
      return {
        lead: `Quote attached · Promo Office quote ${quote} · priced on ${pricedOn}`,
        ticket: null,
        tail: null,
      };
    }
    case "cost_changed": {
      const quote = draft.linkedQuote?.promoOfficeQuoteNumber ?? "not recorded";
      // No `sentAt` gate. The CSR is ticketed on the *first* hold regardless of
      // sending, and slice A can never set `sentAt`, so gating on it hid the
      // ticket link in every case it could ever have been shown.
      const csrTicket = draft.costChange?.csrTicket ?? null;
      return {
        lead: `Promo Office quote ${quote} changed after pricing`,
        ticket: csrTicket
          ? {
              id: csrTicket.id,
              label: `ticket ${ticketNumber(csrTicket.id)} raised to you`,
            }
          : null,
        tail: "Price held",
      };
    }
    case "withdrawn":
      return {
        lead: `Withdrawn ${formatDateOrDash(draft.withdrawnAt)}${draft.withdrawnReason ? ` — ${draft.withdrawnReason}` : ""}`,
        ticket: null,
        tail: null,
      };
  }
}

/**
 * Who in the merchandise team is working on the request: "Claimed by Sam
 * Okafor", or "Not yet claimed" while the open ticket waits for someone to
 * click Claim. Null when there is no ticket, and once it is resolved — the
 * work is done, so who did it is no longer the question the page answers.
 */
export function describeTicketClaim(
  ticket: MerchandiseTeamTicket | null,
): string | null {
  if (!ticket || ticket.resolvedAt) return null;
  return ticket.claimedBy
    ? `Claimed by ${ticket.claimedBy.name}`
    : "Not yet claimed";
}

/**
 * Buy price → margin → Quoted price, with One's own policy price beside it
 * for comparison. The quoted price is the merchandise team's Promo Office
 * selling price, taken as is (Ben's ruling 1, 14 Oct 2026); the margin is what
 * it leaves over the buy price. Every row is a real figure the request holds.
 */
export function draftEstimatePriceLadder(
  pricing: DraftEstimatePricing,
): MoneyRow[] {
  const rows: MoneyRow[] = [
    {
      key: "buy",
      label: "Buy price",
      pence: pricing.buyTotalPence,
      kind: "line",
    },
    {
      key: "margin",
      label: "Margin",
      pence: pricing.marginPence,
      kind: "uplift",
      pct: pricing.marginPct,
    },
    {
      key: "quoted_price",
      label: "Quoted price",
      pence: pricing.ourPricePence,
      kind: "total",
    },
  ];
  // Absent on a draft priced before 15 Oct 2026 and not re-priced since.
  if (pricing.cisPolicyPricePence != null) {
    rows.push({
      key: "cis_policy_price",
      label: `One's policy price (${pricing.marginPolicyName}, for comparison)`,
      pence: pricing.cisPolicyPricePence,
      kind: "aside",
    });
  }
  return rows;
}

/**
 * How the team's quoted price sits against One's policy price, in words:
 * "£8.85 below One's policy price", read after "It is". Null when there is no policy price to
 * compare with. A comparison, never a correction: the quoted price stands.
 */
export function describeQuotedAgainstPolicy(
  pricing: DraftEstimatePricing,
): string | null {
  const policy = pricing.cisPolicyPricePence;
  if (policy == null) return null;
  const difference = pricing.ourPricePence - policy;
  if (difference === 0) return "the same as One's policy price";
  const amount = formatQuotedPrice(Math.abs(difference));
  return difference < 0
    ? `${amount} below One's policy price`
    : `${amount} above One's policy price`;
}

/**
 * The guardrails' flag on the team's price, in the backend's own words. A
 * flag, never a refusal: a thin price is shown and the team's price stands.
 * Null when the price is within the limits or none are configured.
 */
export function draftEstimatePriceFlag(
  pricing: DraftEstimatePricing,
): { tone: "warning" | "danger"; text: string } | null {
  const reason = pricing.verdictReason ? ` — ${pricing.verdictReason}` : "";
  switch (pricing.verdictStatus) {
    case "approval_required":
      return { tone: "warning", text: `Needs approval${reason}` };
    case "blocked":
      return {
        tone: "danger",
        text: `Below the price group's hard limit${reason}`,
      };
    default:
      return null;
  }
}

/**
 * "2 hours", "3 days", "40 minutes" — how long something has been open, in
 * the words a CSR would use. Under a minute reads as "less than a minute".
 */
export function describeOpenFor(
  sinceIso: string,
  now: number = currentTime().getTime(),
): string {
  const minutes = Math.max(
    0,
    Math.floor((now - new Date(sinceIso).getTime()) / 60_000),
  );
  if (minutes < 1) return "less than a minute";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"}`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"}`;
}

/**
 * Why sending is unavailable for this state — the sentence the page renders
 * where the Send button used to be.
 *
 * This replaces `draftEstimateSendability`, which returned `{canSend, reason}`.
 * With slice A having no send at all, every branch of that predicate returned
 * `false`: a compile-time constant dressed as a live check, which every call
 * site still read as one. One function, one job, no boolean. When the basket
 * stream adds sending, a real sendability predicate comes back with a real
 * `true` branch; resurrecting an honest one then is cheaper than carrying a
 * dishonest one until now.
 */
export function draftSendBlockReason(
  request: MerchandiseDraftEstimate,
): string {
  switch (request.state) {
    case "awaiting_quote":
      return "No price yet — waiting on the merchandise team's quote.";
    case "cost_changed":
      return "The Promo Office quote has changed since this was priced. Re-price to take the merchandise team's latest quote, or withdraw the request.";
    case "withdrawn":
      return "This request was withdrawn.";
    case "priced":
      // The honest state, not a fault: the price is agreed and held, and what
      // is missing is a feature rather than a step the CSR has skipped.
      return "Sending merchandise quotes to the customer arrives when they become ordinary quotes in One. Until then the quoted price is recorded here.";
  }
}
